// src/components/chatbot/ChatInput.tsx - UPDATED VERSION
import React, { useRef, useEffect, useState } from 'react';
import { Send, Mic, HelpCircle, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import VoiceOptions from './VoiceOptions';
import VoiceTips from './VoiceTips';

// Import Voice AI Workforce types
import { 
  VoiceCommand, 
  VoiceResponse, 
  VoiceAIConfig 
} from '@voice-ai-workforce/types';

type VoiceMode = 'voice-to-voice' | 'voice-to-text' | 'disabled';

type ChatInputProps = {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSend: () => void;
  isDisabled?: boolean;
  autoFocus?: boolean;
  onVoiceCommand?: (command: VoiceCommand, mode: VoiceMode) => void;
  onVoiceResponse?: (response: VoiceResponse) => void;
  enableVoice?: boolean;
  voiceConfig?: VoiceAIConfig;
};

const ChatInput: React.FC<ChatInputProps> = ({
  value,
  onChange,
  onSend,
  isDisabled = false,
  autoFocus = false,
  onVoiceCommand,
  onVoiceResponse,
  enableVoice = true,
  voiceConfig
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [showVoiceOptions, setShowVoiceOptions] = useState(false);
  const [showVoiceTips, setShowVoiceTips] = useState(false);
  const [voiceMode, setVoiceMode] = useState<VoiceMode>('disabled');
  const [hasSeenTips, setHasSeenTips] = useState(false);
  
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  // Check if user has seen tips before
  useEffect(() => {
    const seenTips = localStorage.getItem('staffluent-voice-tips-seen');
    setHasSeenTips(!!seenTips);
  }, []);
  
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onSend();
    }
  };

  // Handle voice command - receives VoiceCommand object
  const handleVoiceCommand = (command: VoiceCommand, mode: VoiceMode) => {
    console.log('ChatInput received voice command:', command);
    if (onVoiceCommand) {
      onVoiceCommand(command, mode);
    }
    setShowVoiceOptions(false);
  };

  // Handle voice response - receives VoiceResponse object
  const handleVoiceResponse = (response: VoiceResponse) => {
    console.log('ChatInput received voice response:', response);
    if (onVoiceResponse) {
      onVoiceResponse(response);
    }
  };

  const handleModeChange = (mode: VoiceMode) => {
    setVoiceMode(mode);
    if (mode === 'disabled') {
      setShowVoiceOptions(false);
    }
  };

  const handleShowTips = () => {
    setShowVoiceTips(true);
    setShowVoiceOptions(false);
    localStorage.setItem('staffluent-voice-tips-seen', 'true');
    setHasSeenTips(true);
  };

  const handleStartVoiceFromTips = (mode: 'voice-to-voice' | 'voice-to-text') => {
    setVoiceMode(mode);
    setShowVoiceTips(false);
    setShowVoiceOptions(true);
  };
  
  return (
    <>
      <div className="relative">
        {/* Input Area */}
        <div className="p-3 border-t bg-card">
          <div className="flex items-center gap-2">
            <Input
              ref={inputRef}
              type="text"
              placeholder={
                voiceMode === 'disabled' 
                  ? "Type your message or try voice..." 
                  : voiceMode === 'voice-to-voice'
                  ? "Type or speak your message..."
                  : "Type or use voice input..."
              }
              value={value}
              onChange={onChange}
              onKeyPress={handleKeyPress}
              className="flex-1"
              disabled={isDisabled}
            />
            
            {/* Voice Tips Button (for new users) */}
            {enableVoice && !hasSeenTips && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={handleShowTips}
                      disabled={isDisabled}
                      className="relative border-dashed border-blue-300 hover:border-blue-500 bg-blue-50 hover:bg-blue-100"
                    >
                      <div className="absolute -top-1 -right-1">
                        <Sparkles className="h-3 w-3 text-blue-500 animate-pulse" />
                      </div>
                      <HelpCircle className="h-4 w-4 text-blue-600" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>✨ Try voice commands! Click for tips</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {/* Voice Button */}
            {enableVoice && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant={voiceMode !== 'disabled' ? 'default' : 'outline'}
                      size="icon"
                      onClick={() => setShowVoiceOptions(!showVoiceOptions)}
                      disabled={isDisabled}
                      aria-label="Voice Assistant"
                      className={voiceMode !== 'disabled' ? 'bg-blue-600 hover:bg-blue-700' : ''}
                    >
                      <Mic className="h-4 w-4" />
                      {voiceMode !== 'disabled' && (
                        <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>
                      {voiceMode === 'disabled' 
                        ? 'Enable Voice Assistant' 
                        : voiceMode === 'voice-to-voice' 
                        ? 'Voice Conversation Active'
                        : 'Voice Input Active'
                      }
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            
            {/* Send Button */}
            <Button
              size="icon"
              onClick={onSend}
              disabled={isDisabled || !value.trim()}
              aria-label="Send message"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
          
          {/* Voice Mode Status */}
          {enableVoice && voiceMode !== 'disabled' && (
            <div className="mt-2 text-xs text-muted-foreground flex items-center gap-1">
              <Mic className="h-3 w-3" />
              <span>
                {voiceMode === 'voice-to-voice' 
                  ? 'Voice conversation active - Speak naturally' 
                  : 'Voice input active - Speak to type'
                }
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleShowTips}
                className="ml-auto text-xs h-auto p-1 text-blue-600 hover:text-blue-800"
              >
                <HelpCircle className="h-3 w-3 mr-1" />
                Tips
              </Button>
            </div>
          )}
        </div>

        {/* Voice Options Modal */}
        {enableVoice && (
          <VoiceOptions
            isVisible={showVoiceOptions}
            onClose={() => setShowVoiceOptions(false)}
            onVoiceCommand={handleVoiceCommand}
            // @ts-ignore
            onVoiceResponse={handleVoiceResponse}
            currentMode={voiceMode}
            onModeChange={handleModeChange}
            onShowTips={handleShowTips}
            voiceConfig={voiceConfig}
          />
        )}
      </div>

      {/* Voice Tips Modal */}
      {enableVoice && (
        <VoiceTips
          isVisible={showVoiceTips}
          onClose={() => setShowVoiceTips(false)}
          onStartVoice={handleStartVoiceFromTips}
        />
      )}
    </>
  );
};

export default ChatInput;